import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMotionPreference } from './useScrollScene';
import { hasVoicesInteriorScene } from './voicesRoutes';
import './VoicesInteriorScene.css';

export function interiorSceneType({ type, category, name = '' } = {}) {
  if (type === 'agents' || category === 'AI') return 'stack';
  if (type === 'managed') return 'orbit';
  if (category === 'Integration') return 'pair';
  if (category === 'Salesforce' || (!category && name.startsWith('Salesforce '))) return 'square';
  return 'loop';
}

// The reference pageHero leaves the viewport normally; it is not a sticky journey.
export function interiorProgress(top, height) {
  return Math.max(0, Math.min(1, -top / Math.max(1, height))) * 0.65;
}

function InteriorHero({ as: Element, scene, children, className, style, ...props }) {
  const hero = useRef(null);
  const surface = useRef(null);
  const canvas = useRef(null);
  const [reduced] = useMotionPreference();
  const [status, setStatus] = useState('pending');

  useEffect(() => {
    if (reduced) return;
    setStatus('pending');
    const root = hero.current;
    const visual = surface.current;
    const target = canvas.current;
    let renderer;
    let disposed = false;
    let failed = false;
    let loading = false;
    let visible = false;
    let frame = 0;

    const fail = () => {
      if (disposed || failed) return;
      failed = true;
      cancelAnimationFrame(frame);
      frame = 0;
      renderer?.dispose();
      renderer = null;
      setStatus('unavailable');
    };
    const draw = () => {
      frame = 0;
      if (disposed || failed || !visible || document.hidden || !renderer) return;
      const rect = root.getBoundingClientRect();
      const width = visual.clientWidth;
      const height = visual.clientHeight;
      if (!width || !height || rect.bottom <= 0 || rect.top >= window.innerHeight) return;
      try {
        const progress = interiorProgress(rect.top, rect.height);
        const result = renderer.render({ section: scene, progress, width, height, time: 0, pointer: [0, 0] });
        if (result === false) { fail(); return; }
        target.dataset.progress = progress.toFixed(5);
        setStatus('ready');
      } catch { fail(); }
    };
    const wake = () => {
      if (!frame && renderer && visible && !document.hidden && !failed) frame = requestAnimationFrame(draw);
    };
    const load = async () => {
      if (loading || renderer || disposed || failed || document.hidden || !visual.clientWidth || !visual.clientHeight) return;
      loading = true;
      try {
        const { createVoicesScene } = await import('./three/VoicesScene');
        if (disposed || failed) return;
        renderer = createVoicesScene(target);
        wake();
      } catch { fail(); }
    };
    const refresh = () => { if (visible) { load(); wake(); } };
    const onVisibility = () => {
      if (document.hidden) { cancelAnimationFrame(frame); frame = 0; }
      else if (visible) { load(); wake(); }
    };
    const intersection = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting;
      if (visible) { load(); wake(); }
      else { cancelAnimationFrame(frame); frame = 0; }
    }) : null;
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(refresh) : null;
    intersection?.observe(root);
    resize?.observe(root);
    resize?.observe(visual);
    const contextLost = event => { event.preventDefault(); fail(); };
    const startupTimeout = () => { if (!target.hasAttribute('data-progress')) fail(); };
    target.addEventListener('webglcontextlost', contextLost);
    target.addEventListener('voicesstartuptimeout', startupTimeout);
    target.addEventListener('studioerror', fail);
    window.addEventListener('scroll', wake, { passive: true });
    window.addEventListener('resize', refresh, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    if (!intersection) { visible = true; load(); }
    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      intersection?.disconnect();
      resize?.disconnect();
      target.removeEventListener('webglcontextlost', contextLost);
      target.removeEventListener('voicesstartuptimeout', startupTimeout);
      target.removeEventListener('studioerror', fail);
      window.removeEventListener('scroll', wake);
      window.removeEventListener('resize', refresh);
      document.removeEventListener('visibilitychange', onVisibility);
      renderer?.dispose();
    };
  }, [scene, reduced]);

  return <Element {...props} ref={hero} className={`${className} v-interior-hero`} style={{ ...style, backgroundColor: 'var(--vt-bg, #050609)' }} data-interior-scene={scene} data-renderer={reduced ? 'reduced' : status}>
    <div ref={surface} className="v-interior-scene" aria-hidden="true"><canvas ref={canvas} /></div>
    <div className="v-interior-content">{children}</div>
  </Element>;
}

export default function VoicesInteriorScene({ as: Element = 'header', scene = 'loop', className = '', ...props }) {
  const { pathname } = useLocation();
  let path;
  try { path = decodeURIComponent(pathname).toLowerCase().replace(/\/+$/, ''); } catch { path = ''; }
  const enabled = hasVoicesInteriorScene(path);
  if (!enabled) return <Element {...props} className={className} />;
  return <InteriorHero key={`${path}:${scene}`} {...props} as={Element} className={className} scene={scene} />;
}
